// Which variant a product opens on — ONE rule, shared by the storefront
// (src/lib/variants.ts) and the build (scripts/fetch-products.mjs).
//
// Supplier titles often name one variant ("8' X 10' … Area Rug", "… King Bed")
// while the product also carries a 2' X 3' or a Twin at a fraction of the
// price. Opening on the cheapest variant put that price beside the 8' X 10'
// name on the page, in JSON-LD and in the feeds. So the page opens on the
// variant the TITLE names, and only falls back to the cheapest one in stock
// when the title names none. The build prices the head, JSON-LD and feeds with
// the same function, which keeps them equal to the price the page shows.
//
// When the titled variant is sold out the page still opens on it, as sold out,
// with the other options to pick from: the name, the description and the
// measurements all describe that variant, so another variant's price beside
// them would be a false pair (it was, on 93 products).
//
// The matcher reads nothing but the product's own title and option values.
// When a title is ambiguous (it names two values of one option, or none) that
// option is left open: a wrong guess is worse than the cheapest variant.

const NUMBER_WORDS = {
  one: "1", two: "2", three: "3", four: "4", five: "5", six: "6",
  seven: "7", eight: "8", nine: "9", ten: "10", eleven: "11", twelve: "12",
};

/** Lowercase tokens with one spelling for quote marks, "×", "and" and number words. */
function tokens(text) {
  return String(text ?? "")
    .toLowerCase()
    .replace(/[\u2018\u2019\u2032\u00b4`]/g, "'")
    .replace(/[\u201c\u201d\u2033]|''/g, '"')
    .replace(/\u00d7/g, "x")
    .replace(/(\d)[\s-]*(?:inch(?:es)?)\b/g, '$1"')
    .replace(/(\d)[\s-]*(?:feet|foot|ft)\b/g, "$1'")
    // 8' X 10', 8'x10' and 8 x 10 are one size token: 8'x10'
    .replace(/(\d['"]?)\s*(?:x|by)\s*(?=\d)/g, "$1x")
    // "Black And White", "Black/White" and "Black, White" are one value
    .replace(/&|\band\b/g, " ")
    .replace(/[^a-z0-9'".]+/g, " ")
    .replace(/\.(?!\d)/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((t) => NUMBER_WORDS[t] ?? (t === "grey" ? "gray" : t));
}

/**
 * One measure: 2'5" -> 29 in, 8' -> 96 in, 36" -> 36 in. `written` is the
 * number as printed; a bare number has no unit.
 */
function measure(part) {
  const m = /^(\d+(?:\.\d+)?)(?:(')(?:(\d+(?:\.\d+)?)")?|("))?$/.exec(part);
  if (!m) return null;
  const written = Number(m[1]);
  if (m[2]) return { n: written * 12 + Number(m[3] ?? 0), written: m[3] ? null : written, unit: true };
  return { n: written, written, unit: Boolean(m[4]) };
}

/** A size token (24"x36", 2'x3', 8x10) as a list of measures, else null. */
function sizeGroup(token) {
  if (!/^\d[\d.'"]*(?:x\d[\d.'"]*)+$/.test(token)) return null;
  const parts = token.split("x").map(measure);
  return parts.every(Boolean) ? parts : null;
}

// 24" X 36" in a title is the 2'x3' variant: feet and inches compare in
// inches. A side written without a unit ("8x10") may be either.
function sameSides(a, b) {
  const near = (p, q) => Math.abs(p - q) < 0.01;
  return a.every((x, i) => {
    const y = b[i];
    if (x.unit === y.unit) return near(x.n, y.n);
    const [known, bare] = x.unit ? [x, y] : [y, x];
    return near(known.n, bare.n) || near(known.n, bare.n * 12);
  });
}
// A 30" X 24" poster is the 24x30 variant turned on its side.
function sameSize(a, b) {
  if (a.length !== b.length) return false;
  return sameSides(a, b) || (a.length === 2 && sameSides(a, [b[1], b[0]]));
}

/** Every order of a short list. */
function orderings(items) {
  if (items.length < 2) return [items];
  return items.flatMap((item, i) => orderings(items.filter((_, j) => j !== i)).map((rest) => [item, ...rest]));
}

// Options whose bare numbers are a measurement ("Width: 78.74", "Size: 7.5").
const MEASURED_OPTION = /\b(width|height|depth|length|diameter|size)\b/i;
// Options whose bare numbers are counts ("Number of Drawers: 6").
const COUNT_OPTION = /^number of\b/i;
// "Arms: Yes / No" and single letters can never be read out of a title safely.
const UNMATCHABLE = /^(yes|no|true|false|none|[a-z])$/;
// ...except where the option is the letter itself ("Initial or Monogram: J").
const LETTER_OPTION = /\b(initial|monogram|letter)\b/i;
// Bed sizes the supplier spells two ways.
const SAME_VALUE = { "full double": "full", "eastern king": "king" };
const SHAPE_WORDS = new Set(["round", "square", "runner", "oval", "octagon", "hexagon"]);
// A bed sold by width: "Depth: 75.5" is the King of a "... King Bed Frame".
const BED_WIDTHS = { twin: [37, 40], full: [53, 55], queen: [59, 61], king: [75, 78] };
const BED_TITLE = new Set(["bed", "beds", "mattress", "headboard"]);
/** The one bed size a bed's title names, with its width range in inches; null for none, two, or a California king. */
function bedWidth(title) {
  if (!title.list.some((t) => BED_TITLE.has(t))) return null;
  if (/ (?:california|cal) king /.test(title.text)) return null;
  const named = Object.keys(BED_WIDTHS).filter((size) => title.list.includes(size));
  return named.length === 1 ? { size: named[0], range: BED_WIDTHS[named[0]] } : null;
}
// Marks a match on the title's leading width only (see matchValue).
const LEAD_WIDTH = "lead-width:";

/**
 * How `value` of option `name` appears in the title: the matched text (longer
 * is more specific), or null when the title does not name it.
 */
function matchValue(title, name, value) {
  const want = tokens(value);
  if (!want.length) return null;
  const text = want.join(" ");
  // "... Cotton Blend J Throw Blanket": a letter is read only for a monogram option.
  if (LETTER_OPTION.test(name) && /^[a-z]$/.test(text)) return title.list.includes(text) ? text : null;
  if (UNMATCHABLE.test(text)) return null;

  if (want.length === 1 && /^\d+(?:\.\d+)?$/.test(text)) {
    const n = Number(text);
    if (COUNT_OPTION.test(name)) return title.list.includes(String(n)) ? String(n) : null;
    if (!MEASURED_OPTION.test(name)) return null;
    // Inches or feet as printed (4' tree = "4"), or inches the title rounds
    // (a 78.74" table is sold as 79").
    const hit = title.list.find((t) => {
      const m = measure(t);
      return m?.unit && (m.written === n || Math.abs(m.n - n) <= 0.5);
    });
    if (hit) return hit;
    const bed = bedWidth(title);
    return bed && n >= bed.range[0] && n <= bed.range[1] ? bed.size : null;
  }

  if (title.text.includes(` ${text} `)) return text;
  const same = SAME_VALUE[text];
  if (same && title.text.includes(` ${same} `)) return same;

  if (want.length === 1) {
    const size = sizeGroup(text);
    const hit = size && title.list.find((t) => {
      const g = sizeGroup(t);
      return g && sameSize(size, g);
    });
    if (hit) return hit;
    // A title that leads with one measure (69" ... Love Seat) and gives no full
    // size names the first side of a W x D x H value (69" X 37" X 36").
    const lead = measure(title.list[0] ?? "");
    const leadsWithWidth = size?.length === 3 && lead?.unit && size[0].unit && Math.abs(size[0].n - lead.n) < 0.01;
    return leadsWithWidth && !title.list.some((t) => sizeGroup(t)) ? LEAD_WIDTH + title.list[0] : null;
  }

  // "8' Round" is the size of an "8' Aqua Round … Rug".
  if (measure(want[0])?.unit && want.slice(1).every((w) => SHAPE_WORDS.has(w))) {
    return want.every((w) => title.list.includes(w)) ? text : null;
  }

  // A colour list is the same value in any order: "Black, Brown" is the
  // "Brown And Black" of a title.
  const parts = String(value).toLowerCase().split(/\s*(?:[,/&+]|\band\b)\s*/).filter(Boolean);
  if (parts.length === 2 || parts.length === 3) {
    for (const order of orderings(parts)) {
      const listed = tokens(order.join(" ")).join(" ");
      if (title.text.includes(` ${listed} `)) return listed;
    }
  }
  return null;
}

// Options a title can name by a colour's last word.
const COLOUR_OPTION = /\b(colou?r|finish)\b/i;
const PLAIN_COLOURS = new Set(
  "black white gray blue green red brown beige ivory cream gold silver taupe tan orange yellow pink purple navy teal turquoise charcoal bronze brass copper".split(" "),
);

/**
 * Every part of a colour value ("Dark Taupe,Silver" -> dark taupe / silver) has
 * its last word in the title, and at least one part had a leading word dropped.
 */
function headWordsNamed(title, name, value) {
  if (!COLOUR_OPTION.test(name)) return false;
  const parts = String(value).toLowerCase().split(/\s*(?:[,/&+]|\band\b)\s*/).map((p) => tokens(p)).filter((p) => p.length);
  if (!parts.length || parts.every((p) => p.length === 1)) return false;
  const named = parts.every((p) => {
    const head = p[p.length - 1];
    return /^[a-z]{3,}$/.test(head) && title.list.includes(head);
  });
  if (!named) return false;
  // "Golden White" is not the "Blue and White" of a title: every plain colour
  // the title names has to be in the value too.
  const words = new Set(parts.flat());
  return title.list.every((t) => !PLAIN_COLOURS.has(t) || words.has(t));
}

/**
 * The value of one option the title names, as a one-item list, or undefined.
 * Several W x D x H values that share the title's leading width come back
 * together: the title names the width, not which of them.
 */
function namedValues(title, name, values) {
  const all = [];
  for (const value of values) {
    const matched = matchValue(title, name, value);
    if (matched) all.push({ value, matched });
  }
  if (!all.length) {
    // No value is spelled out in full. Titles often name a colour by its last
    // word only ("White … Dresser" for "Bone White", "Taupe and Silver" for
    // "Dark Taupe,Silver"). Accept that only when exactly one value fits.
    const loose = values.filter((value) => headWordsNamed(title, name, value));
    return loose.length === 1 ? loose : undefined;
  }
  const hits = all.filter((h) => !h.matched.startsWith(LEAD_WIDTH));
  if (!hits.length) return all.map((h) => h.value);
  hits.sort((a, b) => b.matched.length - a.matched.length);
  const [best, ...rest] = hits;
  // "Dark Brown" also contains "Brown": the longer value wins. A second value
  // named elsewhere in the title ("Black Chair With White Legs") is ambiguous.
  const remaining = title.text.split(` ${best.matched} `).join(" | ");
  if (rest.some((h) => h.matched === best.matched || remaining.includes(` ${h.matched} `))) return undefined;
  return [best.value];
}

const priceOf = (v) => Number.parseFloat(v?.price?.amount ?? "0");
const cheapest = (list) => list.reduce((best, v) => (priceOf(v) < priceOf(best) ? v : best));
const optionValue = (v, name) => v.selectedOptions?.find((o) => o.name === name)?.value;

/**
 * The variants the title can mean: those carrying every option value the title
 * names. In stock when the titled variant is; the titled variant itself, sold
 * out, when it is not (its own price and "unavailable" beside its name, never
 * another size's price). An option the title does not name narrows nothing.
 */
export function titleCandidates(productTitle, variants) {
  const all = variants || [];
  const inStock = all.filter((v) => v.availableForSale);
  const buyable = inStock.length ? inStock : all;
  if (all.length < 2) return buyable;

  const options = new Map();
  for (const v of all) {
    for (const o of v.selectedOptions || []) {
      if (!options.has(o.name)) options.set(o.name, new Set());
      options.get(o.name).add(o.value);
    }
  }
  const list = tokens(productTitle);
  const title = { list, text: ` ${list.join(" ")} ` };

  const named = [];
  for (const [name, values] of options) {
    if (values.size < 2) continue;
    const hit = namedValues(title, name, [...values]);
    if (hit !== undefined) named.push([name, hit]);
  }
  if (!named.length) return buyable;
  const narrow = (from, strict) => {
    let pool = from;
    for (const [name, values] of named) {
      const narrowed = pool.filter((v) => values.includes(optionValue(v, name)));
      if (narrowed.length) pool = narrowed;
      else if (strict) return null;
    }
    return pool;
  };
  // In stock as titled; else the titled variant, sold out; a combination that
  // does not exist at all narrows as far as it can among what is on offer.
  return narrow(buyable, true) ?? narrow(all, true) ?? narrow(buyable, false);
}

/**
 * The variant a product opens on, and the one whose price and availability the
 * head, JSON-LD and feeds carry: the cheapest variant the title can mean. It is
 * in stock whenever one the title can mean is.
 */
export function pickDefaultVariant(productTitle, variants) {
  const pool = titleCandidates(productTitle, variants);
  return pool.length ? cheapest(pool) : undefined;
}

/**
 * { low, high } price over the in-stock variants, or null when none is in
 * stock. Listings say "From $low" when the two differ.
 */
export function inStockPriceRange(variants) {
  const prices = (variants || []).filter((v) => v.availableForSale).map(priceOf);
  if (!prices.length) return null;
  return { low: Math.min(...prices), high: Math.max(...prices) };
}

// Options that change how large a piece is, or how many parts it has.
const DIMENSION_OPTION = /size|width|height|depth|length|diameter|dimension|^number of/i;

/**
 * Whether the supplier's one title and one set of measurements describe
 * `variant`: the size facts on a page are only shown for a variant they are
 * known to belong to. True when the variants do not differ in size at all.
 * False once a size (or drawer count) other than the one the title names is
 * selected, and false for every variant when the title names no size: nothing
 * then says which of them the supplier measured.
 */
export function titleDescribesVariant(productTitle, variants, variant) {
  const size = (v) => (v.selectedOptions || []).filter((o) => DIMENSION_OPTION.test(o.name));
  const key = (v) => JSON.stringify(v.selectedOptions);
  // Stock is ignored here: the question is what the title names, not what can be bought.
  const sized = (variants || []).map((v) => ({ ref: v, availableForSale: true, price: v.price, selectedOptions: size(v) }));
  if (!variant || new Set(sized.map(key)).size < 2) return true;
  const named = titleCandidates(productTitle, sized);
  if (new Set(named.map(key)).size > 1) return false;
  return named.some((v) => v.ref === variant || (variant.id != null && v.ref.id === variant.id));
}

// A file uploaded a second time keeps its name and gains another "_<uuid>".
const UPLOAD_ID = /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\.\w+$)/i;
const imageFile = (url) => String(url).split("?")[0].replace(UPLOAD_ID, "");

/**
 * Whether two Shopify image URLs show one picture. URLs for one file differ
 * only in their query (?v=, &width=). The supplier also uploads the lead photo
 * again as a variant's image, which Shopify stores under the same name with a
 * different upload id (612851_12_kldOylHy_1000x1000_68ddae64-….jpg and
 * …_4a1be52e-….jpg): compared as whole paths, that photo entered the gallery
 * twice and was downloaded twice.
 */
export function sameImageUrl(a, b) {
  return !!a && !!b && imageFile(a) === imageFile(b);
}

// Options that say how large a piece is, not what it looks like.
const SIZE_OPTION = /size|width|height|depth|length|diameter/i;

/**
 * Whether the variants differ in more than size: a colour, a finish, a number
 * of drawers. Such a product opens on its variant's own photo, because the
 * lead photo is often another colour. One sold in sizes only keeps the lead
 * photo until the shopper picks a size.
 */
export function variantsDifferInLook(variants) {
  const list = variants || [];
  const names = new Set(list.flatMap((v) => (v.selectedOptions || []).map((o) => o.name)));
  return [...names].some((name) => !SIZE_OPTION.test(name) && new Set(list.map((v) => optionValue(v, name))).size > 1);
}

// The supplier names every photo <sku>_<n>: 517450_1_…jpg is the first photo
// of SKU 517450, the whole piece. Later ones (517450_9) are often a dimension
// drawing, a cropped corner or a close-up, and nothing else in the data says so.
const FIRST_PHOTO = /\/[^/_]+_1[_.][^/]*$/;

/**
 * The photo a product page opens on, known to the build as well, so the head
 * preloads the same file and og:image / JSON-LD show the variant the title
 * names. The opening variant's own photo when the variants differ in look and
 * that photo is the first of its SKU; else the product's lead photo (`leadUrl`).
 */
export function openingImageUrl(productTitle, variants, leadUrl) {
  const list = variants || [];
  if (list.length < 2 || !variantsDifferInLook(list)) return leadUrl;
  const own = pickDefaultVariant(productTitle, list)?.image?.url;
  if (!own || sameImageUrl(own, leadUrl) || !FIRST_PHOTO.test(imageFile(own))) return leadUrl;
  return own;
}
